import React, { useState, useMemo, useEffect } from 'react'
import Modal from './MyModal'
import { API_BASE_URL, X_API_KEY } from '../../services/api'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import ImageWithApiKey from './ImageWithApiKey'

interface GalleryItem {
  id: number
  fileUrl?: string
  fileName: string
  cover?: boolean
  status: number
  userId: number
}

interface GalleryProps {
  gallery: GalleryItem[]
  coverImage: GalleryItem | undefined
  gender: string | undefined
  statusToShow: number | number[]
}

const Gallery: React.FC<GalleryProps> = ({
  gallery,
  coverImage,
  gender,
  statusToShow,
}) => {
  const normalizedStatusToShow = useMemo(() => {
    return Array.isArray(statusToShow) ? statusToShow : [statusToShow]
  }, [statusToShow])

  const [isOpen, setIsOpen] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)

  const defaultImageUrl = (gender: string | undefined) => {
    const baseUrl = window.location.origin
    switch (gender) {
      case 'man':
        return `${baseUrl}/images/silouhette-male.jpg`
      case 'woman':
        return `${baseUrl}/images/silouhette-female.jpg`
      case 'transgender':
        return `${baseUrl}/images/silouhette-transgender.jpg`
      case 'couple':
        return `${baseUrl}/images/silouhette-couple.jpg`
      default:
        return `${baseUrl}/images/silouhette.jpg`
    }
  }

  const getImageUrl = (item: GalleryItem) => {
    if (item.fileUrl) return item.fileUrl
    if (item.fileName && item.userId) {
      return `${API_BASE_URL}/images/${item.userId}/${item.fileName}`
    }
    return defaultImageUrl(gender)
  }

  const PrevArrow = (props: any) => (
    <div
      className='absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-3 rounded-full cursor-pointer hover:bg-opacity-75 z-10 transition-all text-white'
      onClick={props.onClick}
    >
      <FaChevronLeft size={24} />
    </div>
  )

  const NextArrow = (props: any) => (
    <div
      className='absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-3 rounded-full cursor-pointer hover:bg-opacity-75 transition-all text-white'
      onClick={props.onClick}
    >
      <FaChevronRight size={24} />
    </div>
  )

  const handleImageError = (fileUrl: string) => {
    console.error('🚨 Image failed to load:', fileUrl)
  }

  const filteredGallery = useMemo(() => {
    return gallery.filter(item => normalizedStatusToShow.includes(item.status))
  }, [gallery, normalizedStatusToShow])

  const sortedGallery = useMemo(() => {
    if (filteredGallery.length > 0) {
      const sorted = [...filteredGallery].sort(b => (b.cover === true ? -1 : 1))
      return sorted
    } else {
      return [
        {
          id: 0,
          fileName: 'Default Image',
          status: normalizedStatusToShow[0],
          userId: 0,
        },
      ]
    }
  }, [filteredGallery, normalizedStatusToShow])

  const openModal = (index: number) => {
    setPhotoIndex(index)
    setIsOpen(true)
  }

  const handlePrev = () => {
    setPhotoIndex(
      (photoIndex + sortedGallery.length - 1) % sortedGallery.length,
    )
  }

  const handleNext = () => {
    setPhotoIndex((photoIndex + 1) % sortedGallery.length)
  }

  return (
    <div onContextMenu={e => e.preventDefault()} className='gallery-container'>
      {sortedGallery.length > 1 ? (
        <Slider
          dots={false}
          infinite={true}
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
          adaptiveHeight={false}
          arrows={true}
          prevArrow={<PrevArrow />}
          nextArrow={<NextArrow />}
        >
          {sortedGallery.map((item, index) => (
            <div
              key={item.id}
              onClick={() => openModal(index)}
              className='h-[600px]'
            >
              <ImageWithApiKey
                imageUrl={getImageUrl(item)}
                apiKey={X_API_KEY}
                className='w-full object-cover rounded-lg cursor-pointer'
                minHeight='600px'
                onError={() => handleImageError(getImageUrl(item))}
              />
            </div>
          ))}
        </Slider>
      ) : (
        <div key={sortedGallery[0].id} className='h-[600px]'>
          <ImageWithApiKey
            imageUrl={getImageUrl(sortedGallery[0])}
            apiKey={X_API_KEY}
            className='w-full object-cover rounded-lg cursor-pointer'
            minHeight='600px'
            onError={() => handleImageError(getImageUrl(sortedGallery[0]))}
          />
        </div>
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        size='large'
        showNavigation={sortedGallery.length > 1}
        onPrev={handlePrev}
        onNext={handleNext}
      >
        <div className='w-full'>
          <ImageWithApiKey
            imageUrl={getImageUrl(sortedGallery[photoIndex])}
            apiKey={X_API_KEY}
            className='max-h-[90vh] mx-auto rounded-lg'
            onError={() =>
              handleImageError(getImageUrl(sortedGallery[photoIndex]))
            }
          />
        </div>
      </Modal>
    </div>
  )
}

export default Gallery
